import { useState } from 'react';
import httpClient from "../lib/httpClient";
import {ReelUploadJobRequest} from "@/services/reelUploadService";

export interface SlackNotificationRequest {
  showName?: string;
  userName?: string;
  userId?: string;
  action?: 'PUBLISH_REQUESTED' | 'UNPUBLISH_REQUESTED' | 'SCHEDULE_REQUESTED' | 'UNSCHEDULE_REQUESTED' | 'PLATFORM_RELEASE_REQUESTED' | 'PLATFORM_WITHDRAW_REQUESTED' | 'EPISODE_REUPLOAD' | 'SUBTITLE_REUPLOAD';
  timestamp?: string; // ISO string
  scheduledDate?: string; // ISO string, for SCHEDULE_REQUESTED and PLATFORM_RELEASE_REQUESTED
  requestedTime?: string; // ISO string
  episodeId?: string; // For episode and subtitle reupload operations
  tenant?: string; // For platform-specific release/withdraw (e.g. "reels", "shortfree")
  releaseStrategy?: string | null; // Release strategy id selected for platform release
  // Device platforms the release/withdraw applies to (uppercase DevicePlatform names,
  // e.g. ["ANDROID","IOS"]). Omitted ⇒ backend treats the action as all platforms.
  platforms?: string[];
}

export const useSlackNotification = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sendNotification = async (showId: string, data: SlackNotificationRequest) => {
    setLoading(true);
    setError(null);

    try {
      console.log("sending slack notification");
      return await httpClient.post(`/api/v1/reels-cms/slack-notification/${showId}`, data);
    } catch (error) {
      console.error('Error sending Slack notification:', error);
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const sendPublishRequest = async (showId: string, showName: string, userName: string, userId: string) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'PUBLISH_REQUESTED',
      timestamp: new Date().toISOString(),
      requestedTime: new Date().toISOString()
    };
    return sendNotification(showId, data);
  };

  const sendUnpublishRequest = async (showId: string, showName: string, userName: string, userId: string) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'UNPUBLISH_REQUESTED',
      timestamp: new Date().toISOString()
    };
    return sendNotification(showId, data);
  };

  const sendScheduleRequest = async (showId: string, showName: string, userName: string, userId: string, scheduledDate: string) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'SCHEDULE_REQUESTED',
      timestamp: new Date().toISOString(),
      scheduledDate,
      requestedTime: new Date().toISOString()
    };
    return sendNotification(showId, data);
  };

  const sendUnscheduleRequest = async (showId: string, showName: string, userName: string, userId: string) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'UNSCHEDULE_REQUESTED',
      timestamp: new Date().toISOString()
    };
    return sendNotification(showId, data);
  };

  const sendPlatformReleaseRequest = async (
    showId: string,
    showName: string,
    userName: string,
    userId: string,
    tenant: string,
    scheduledDate?: string,
    releaseStrategy?: string | null,
    platforms?: string[]
  ) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'PLATFORM_RELEASE_REQUESTED',
      timestamp: new Date().toISOString(),
      tenant,
      releaseStrategy,
      ...(platforms && platforms.length > 0 && { platforms }),
      ...(scheduledDate && { scheduledDate, requestedTime: new Date().toISOString() })
    };
    return sendNotification(showId, data);
  };

  const sendPlatformWithdrawRequest = async (
    showId: string,
    showName: string,
    userName: string,
    userId: string,
    tenant: string,
    platforms?: string[]
  ) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'PLATFORM_WITHDRAW_REQUESTED',
      timestamp: new Date().toISOString(),
      tenant,
      ...(platforms && platforms.length > 0 && { platforms })
    };
    return sendNotification(showId, data);
  };

  const sendEpisodeReuploadNotification = async (showId: string, showName: string, userName: string, userId: string, episodeId: string) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'EPISODE_REUPLOAD',
      timestamp: new Date().toISOString(),
      episodeId
    };
    return sendNotification(showId, data);
  };

  const sendSubtitleReuploadNotification = async (showId: string, showName: string, userName: string, userId: string, episodeId: string) => {
    const data: SlackNotificationRequest = {
      showName,
      userName,
      userId,
      action: 'SUBTITLE_REUPLOAD',
      timestamp: new Date().toISOString(),
      episodeId
    };
    return sendNotification(showId, data);
  };

  return {
    sendNotification,
    sendPublishRequest,
    sendUnpublishRequest,
    sendScheduleRequest,
    sendUnscheduleRequest,
    sendPlatformReleaseRequest,
    sendPlatformWithdrawRequest,
    sendEpisodeReuploadNotification,
    sendSubtitleReuploadNotification,
    loading,
    error
  };
};
